import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  saveErrorToast,
  type PageTab,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import VersionHistory from "@components/VersionHistory";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import {
  useV2GetAccountQuery,
  useV2GetRateTableQuery,
  useV2ListCurrencysForAccountQuery,
} from "@generated";
import { RateTableForm } from "../RateTableForm/RateTableForm";
import { ResourceRatesTab } from "../ResourceRatesTab/ResourceRatesTab";
import { useRateTableMutations, useRateTablesList } from "../api";
import { emptyRateTableForm } from "../types";
import type { RateTableFormValues } from "../types";

const FORM_ID = "rate-table-form";

type Tab = "form" | "resources" | "versionHistory";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useRateTableMutations();
  const [activeTab, setActiveTab] = useState<Tab>("form");

  const { hasPrivilege } = usePrivilegeCheck();
  const canViewAuditLogs = hasPrivilege("settings.audit_logs", [
    "view",
    "manage",
  ]);
  const canManageAuditLogs = hasPrivilege("settings.audit_logs", ["manage"]);

  // Only for VersionHistory's date/time format strings.
  const { data: accountData } = useV2GetAccountQuery(
    {},
    { skip: !canViewAuditLogs }
  );

  const tabs: PageTab[] = [
    { key: "form", label: "Rate Table" },
    { key: "resources", label: "Resource Rates" },
    ...(canViewAuditLogs
      ? [{ key: "versionHistory", label: "Version History" }]
      : []),
  ];

  const { data, isLoading, refetch } = useV2GetRateTableQuery(
    {
      id: id,
      include: ["currency"],
    },
    { refetchOnMountOrArgChange: true }
  );
  const { data: currencyData } = useV2ListCurrencysForAccountQuery({
    filter: { active: true },
  });
  const { pageMeta } = useRateTablesList({ filter: { active: true } });

  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<RateTableFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const currencyOptions = useMemo(
    () =>
      (currencyData?.data ?? []).map((c: any) => ({
        value: String(c.id),
        label: c.attributes?.name ?? "",
      })),
    [currencyData]
  );

  const loadedInitial = useMemo<RateTableFormValues>(() => {
    const rt: any = data?.data;
    if (!rt) return emptyRateTableForm;
    const attrs = rt.attributes ?? {};
    const currencyId = rt.relationships?.currency?.data?.id;
    return {
      name: attrs.name ?? "",
      currencyId: currencyId != null ? String(currencyId) : "",
      accountingCode: attrs["accounting-code"] ?? "",
      default: !!attrs.default,
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: RateTableFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update rate table", err);
      setToast(saveErrorToast(err, "rate table"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rate-table-edit-layout">
      <AdminFormPage
        title="Edit Rate Table"
        formId={activeTab === "form" ? FORM_ID : undefined}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as Tab)}
      >
        {activeTab === "form" && (
          <RateTableForm
            key={formKey}
            id={FORM_ID}
            initialValues={initialValues}
            currencyOptions={currencyOptions}
            showCurrencySelect={currencyOptions.length > 1}
            showDefaultToggle={pageMeta.recordCount > 1}
            isSubmitting={submitting}
            onSubmit={handleSubmit}
          />
        )}
        {activeTab === "resources" && <ResourceRatesTab rateTableId={id} />}
        {activeTab === "versionHistory" && canViewAuditLogs && (
          <div className="admin-list-card--bare">
            <VersionHistory
              filter={{ "context-type": "RateTable", "context-id": id }}
              permission={canManageAuditLogs}
              account={accountData?.data}
            />
          </div>
        )}
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Edit;
